import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';

type MailProvider = 'smtp' | 'sendgrid';
type MailAccountStatus = 'active' | 'inactive';

interface MailAccount {
  id: string;
  name: string;
  provider: MailProvider;
  status: MailAccountStatus;
  defaultForOutbound: boolean;
  fromAddress: string;
  replyToAddress?: string;
  inboundAliases: string[];
  smtpHost?: string;
  smtpPort?: number;
  secretRef?: {
    smtpUserSecret?: string;
    smtpPassSecret?: string;
    sendgridApiKeySecret?: string;
  };
  sendgridMeta?: {
    accountLabel?: string;
    verifiedDomain?: string;
  };
}

const DEFAULT_FORM: Omit<MailAccount, 'id'> = {
  name: '',
  provider: 'smtp',
  status: 'active',
  defaultForOutbound: false,
  fromAddress: '',
  replyToAddress: '',
  inboundAliases: [],
  smtpHost: '',
  smtpPort: 465,
  secretRef: {
    smtpUserSecret: '',
    smtpPassSecret: '',
    sendgridApiKeySecret: '',
  },
  sendgridMeta: {
    accountLabel: '',
    verifiedDomain: '',
  },
};

export default function MailAccountSettingsPage() {
  const [items, setItems] = useState<MailAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<MailAccount, 'id'>>(DEFAULT_FORM);
  const [inboundAliasesText, setInboundAliasesText] = useState('');

  const manageMailAccounts = useMemo(
    () => httpsCallable(functions, 'manageMailAccounts'),
    []
  );

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await manageMailAccounts({ action: 'list' });
      const data = result.data as { success: boolean; items: MailAccount[] };
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (loadError) {
      console.error('Failed to load mail accounts', loadError);
      setError('メールアカウント一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [manageMailAccounts]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const resetForm = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setInboundAliasesText('');
  };

  const handleEdit = (item: MailAccount) => {
    setEditingId(item.id);
    setForm({
      ...DEFAULT_FORM,
      ...item,
      secretRef: {
        smtpUserSecret: item.secretRef?.smtpUserSecret || '',
        smtpPassSecret: item.secretRef?.smtpPassSecret || '',
        sendgridApiKeySecret: item.secretRef?.sendgridApiKeySecret || '',
      },
      sendgridMeta: {
        accountLabel: item.sendgridMeta?.accountLabel || '',
        verifiedDomain: item.sendgridMeta?.verifiedDomain || '',
      },
    });
    setInboundAliasesText((item.inboundAliases || []).join('\n'));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setStatusMessage('');
    try {
      const payload = {
        ...form,
        inboundAliases: inboundAliasesText
          .split(/\n|,|;/)
          .map((value) => value.trim())
          .filter(Boolean),
      };

      if (editingId) {
        await manageMailAccounts({ action: 'update', accountId: editingId, payload });
      } else {
        await manageMailAccounts({ action: 'create', payload });
      }
      setStatusMessage(editingId ? 'メールアカウントを更新しました。' : 'メールアカウントを作成しました。');
      await loadAccounts();
      resetForm();
    } catch (saveError) {
      console.error('Failed to save mail account', saveError);
      setError('保存に失敗しました。入力内容をご確認ください。');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (accountId: string) => {
    if (!window.confirm('このメールアカウントを削除（無効化）しますか？')) return;
    setError('');
    setStatusMessage('');
    try {
      await manageMailAccounts({ action: 'delete', accountId });
      setStatusMessage('メールアカウントを削除しました。');
      await loadAccounts();
      if (editingId === accountId) {
        resetForm();
      }
    } catch (deleteError) {
      console.error('Failed to delete mail account', deleteError);
      setError('削除に失敗しました。');
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">メールアカウント管理</h2>
        <p className="mt-2 text-sm text-gray-600">
          送受信で利用するアカウント（SMTP/SendGrid）を管理します。APIキー本体はSecretに保存し、ここには参照名のみを保持してください。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{editingId ? 'メールアカウント編集' : 'メールアカウント追加'}</h3>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">名称</label>
            <input className="w-full rounded border border-gray-300 px-3 py-2" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="例: サポート窓口" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Provider</label>
              <select className="w-full rounded border border-gray-300 px-3 py-2" value={form.provider} onChange={(e) => setForm((prev) => ({ ...prev, provider: e.target.value as MailProvider }))}>
                <option value="smtp">SMTP</option>
                <option value="sendgrid">SendGrid</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">状態</label>
              <select className="w-full rounded border border-gray-300 px-3 py-2" value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as MailAccountStatus }))}>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">送信元アドレス (fromAddress)</label>
            <input className="w-full rounded border border-gray-300 px-3 py-2" type="email" value={form.fromAddress} onChange={(e) => setForm((prev) => ({ ...prev, fromAddress: e.target.value }))} placeholder="support@example.com" />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">返信先アドレス (replyToAddress)</label>
            <input className="w-full rounded border border-gray-300 px-3 py-2" type="email" value={form.replyToAddress || ''} onChange={(e) => setForm((prev) => ({ ...prev, replyToAddress: e.target.value }))} placeholder="reply@example.com" />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">受信対象エイリアス (改行/カンマ区切り)</label>
            <textarea className="w-full min-h-[110px] rounded border border-gray-300 px-3 py-2" value={inboundAliasesText} onChange={(e) => setInboundAliasesText(e.target.value)} placeholder={'support@example.com\ninfo@example.jp'} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">SMTP Host</label>
              <input className="w-full rounded border border-gray-300 px-3 py-2" value={form.smtpHost || ''} onChange={(e) => setForm((prev) => ({ ...prev, smtpHost: e.target.value }))} placeholder="smtp.sendgrid.net" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">SMTP Port</label>
              <input className="w-full rounded border border-gray-300 px-3 py-2" type="number" value={form.smtpPort || 465} onChange={(e) => setForm((prev) => ({ ...prev, smtpPort: Number(e.target.value) || 465 }))} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">SMTP USER Secret名</label>
              <input className="w-full rounded border border-gray-300 px-3 py-2" value={form.secretRef?.smtpUserSecret || ''} onChange={(e) => setForm((prev) => ({ ...prev, secretRef: { ...prev.secretRef, smtpUserSecret: e.target.value } }))} placeholder="SMTP_USER_SUPPORT" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">SMTP PASS Secret名</label>
              <input className="w-full rounded border border-gray-300 px-3 py-2" value={form.secretRef?.smtpPassSecret || ''} onChange={(e) => setForm((prev) => ({ ...prev, secretRef: { ...prev.secretRef, smtpPassSecret: e.target.value } }))} placeholder="SMTP_PASS_SUPPORT" />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">SendGrid API Key Secret名</label>
            <input className="w-full rounded border border-gray-300 px-3 py-2" value={form.secretRef?.sendgridApiKeySecret || ''} onChange={(e) => setForm((prev) => ({ ...prev, secretRef: { ...prev.secretRef, sendgridApiKeySecret: e.target.value } }))} placeholder="SENDGRID_API_KEY_SUPPORT" />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.defaultForOutbound} onChange={(e) => setForm((prev) => ({ ...prev, defaultForOutbound: e.target.checked }))} />
            既定の送信アカウントにする
          </label>

          <div className="flex gap-3 pt-2">
            <button className="rounded bg-emerald-600 px-4 py-2 text-white text-sm font-semibold disabled:opacity-60" onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : editingId ? '更新' : '追加'}
            </button>
            <button className="rounded border border-gray-300 px-4 py-2 text-sm" onClick={resetForm}>
              クリア
            </button>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">登録済みアカウント</h3>
          {loading ? (
            <p className="text-sm text-gray-500">読み込み中...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">登録されたメールアカウントはありません。</p>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.id} className="rounded-xl border border-gray-200 p-4 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.fromAddress}</p>
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.provider} / {item.status}
                      {item.defaultForOutbound ? ' / default' : ''}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600">
                    受信: {(item.inboundAliases || []).join(', ') || '-'}
                  </div>
                  <div className="flex gap-2">
                    <button className="rounded border border-gray-300 px-3 py-1.5 text-xs" onClick={() => handleEdit(item)}>
                      編集
                    </button>
                    <button className="rounded border border-red-300 text-red-700 px-3 py-1.5 text-xs" onClick={() => handleDelete(item.id)}>
                      削除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {statusMessage && <p className="text-sm text-emerald-700">{statusMessage}</p>}
    </div>
  );
}
